/**
 * PracticesEnrollmentsPage
 *
 * Practice Enrollments section: enrollment overview (all providers), application progress,
 * billing-through relationships, and the enrollment PDF generator.
 *
 * PHI-safe for overview tabs: provider/payer names and operational status only; no patient data.
 * The Enrollment PDFs tab handles sensitive credentialing PDFs under role + bot authorization.
 *
 * Lifecycle: Not started | In progress | Submitted | Pending payer response | Approved | Denied
 * Billing through: provider bills under another entity (group/practice); effective dates and payers impacted.
 */

import {
  Box,
  Button,
  Container,
  Group,
  Pagination,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconChartBar,
  IconCreditCard,
  IconFileText,
  IconPlus,
  IconProgress,
} from '@tabler/icons-react';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BillingThroughTable,
  EnrollmentEmptyState,
  EnrollmentProgressTable,
  EnrollmentsFilters,
  EnrollmentsOverviewTable,
  NewEnrollmentModal,
} from './components';
import {
  mockBillingThroughRelationships,
  mockBillingEntities,
  mockEnrollments,
  mockPayers,
  mockProviders,
} from './data/mockEnrollmentsData';
import { useEnrollmentsFiltering } from './hooks/useEnrollmentsFiltering';
import type { Enrollment, EnrollmentsFilters as EnrollmentsFiltersType } from './types';
import { defaultEnrollmentsFilters } from './types';
import { Loading } from '../../components/Loading';
import { LocalhostOnlyBadge } from '../../components/LocalhostOnlyBadge';
import { ENROLLMENT_PDF_TAB } from '../../features/enrollment-pdf/enrollmentPdfRoutes';

const EnrollmentPdfRunsPanel = lazy(async () => {
  const m = await import('../../features/enrollment-pdf');
  return { default: m.EnrollmentPdfRunsPanel };
});

const PAGE_SIZE = 10;

const OVERVIEW_TABS = new Set(['overview', 'progress', 'billing-through', ENROLLMENT_PDF_TAB]);

export function PracticesEnrollmentsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromQuery = searchParams.get('tab');
  const initialTab =
    tabFromQuery && OVERVIEW_TABS.has(tabFromQuery) ? tabFromQuery : 'overview';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [enrollments, setEnrollments] = useState<Enrollment[]>(mockEnrollments);
  const [filters, setFilters] = useState<EnrollmentsFiltersType>(defaultEnrollmentsFilters);
  const [overviewPage, setOverviewPage] = useState(1);
  const [progressPage, setProgressPage] = useState(1);
  const [newEnrollmentModalOpen, setNewEnrollmentModalOpen] = useState(false);

  useEffect(() => {
    if (tabFromQuery && OVERVIEW_TABS.has(tabFromQuery)) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  const { filteredEnrollments, hasActiveFilters } = useEnrollmentsFiltering(enrollments, filters);

  const handleNewEnrollmentCreated = useCallback((enrollment: Enrollment) => {
    setEnrollments((prev) => [enrollment, ...prev]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultEnrollmentsFilters);
    setOverviewPage(1);
    setProgressPage(1);
  }, []);

  const handleTabChange = useCallback(
    (value: string | null) => {
      const next = value ?? 'overview';
      setActiveTab(next);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'overview') params.delete('tab');
          else params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const showOverviewEmpty = filteredEnrollments.length === 0;
  const emptyVariant = hasActiveFilters ? 'no-results' : 'no-enrollments';
  const showNewEnrollment = activeTab !== ENROLLMENT_PDF_TAB;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Group gap="xs" align="center">
              <Title order={2}>Practice Enrollments</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text c="dimmed" size="sm">
              Payer enrollments, billing relationships, and enrollment PDF generation
            </Text>
          </div>
          {showNewEnrollment ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setNewEnrollmentModalOpen(true)}>
              New enrollment
            </Button>
          ) : null}
        </Group>

        <NewEnrollmentModal
          opened={newEnrollmentModalOpen}
          onClose={() => setNewEnrollmentModalOpen(false)}
          onCreated={handleNewEnrollmentCreated}
          providers={mockProviders}
          payers={mockPayers}
          billingEntities={mockBillingEntities}
        />

        <Tabs value={activeTab} onChange={handleTabChange} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Enrollment overview
            </Tabs.Tab>
            <Tabs.Tab value="progress" leftSection={<IconProgress size={16} />}>
              Application progress
            </Tabs.Tab>
            <Tabs.Tab value="billing-through" leftSection={<IconCreditCard size={16} />}>
              Billing through
            </Tabs.Tab>
            <Tabs.Tab value={ENROLLMENT_PDF_TAB} leftSection={<IconFileText size={16} />}>
              Enrollment PDFs
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" mt="md">
            <Stack gap="md">
              <EnrollmentsFilters filters={filters} onChange={setFilters} />
              {showOverviewEmpty ? (
                <Paper withBorder p="lg">
                  <EnrollmentEmptyState
                    variant={emptyVariant}
                    onClearFilters={handleClearFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </Paper>
              ) : (
                <>
                  <Paper withBorder p="lg">
                    <EnrollmentsOverviewTable
                      enrollments={filteredEnrollments}
                      page={overviewPage}
                      pageSize={PAGE_SIZE}
                    />
                  </Paper>
                  {filteredEnrollments.length > PAGE_SIZE && (
                    <Group justify="center">
                      <Pagination
                        total={Math.ceil(filteredEnrollments.length / PAGE_SIZE)}
                        value={overviewPage}
                        onChange={setOverviewPage}
                      />
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="progress" mt="md">
            <Stack gap="md">
              <EnrollmentsFilters filters={filters} onChange={setFilters} />
              {filteredEnrollments.length === 0 ? (
                <Paper withBorder p="lg">
                  <EnrollmentEmptyState
                    variant={emptyVariant}
                    onClearFilters={handleClearFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </Paper>
              ) : (
                <>
                  <Paper withBorder p="lg">
                    <EnrollmentProgressTable
                      enrollments={filteredEnrollments}
                      page={progressPage}
                      pageSize={PAGE_SIZE}
                    />
                  </Paper>
                  {filteredEnrollments.length > PAGE_SIZE && (
                    <Group justify="center">
                      <Pagination
                        total={Math.ceil(filteredEnrollments.length / PAGE_SIZE)}
                        value={progressPage}
                        onChange={setProgressPage}
                      />
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="billing-through" mt="md">
            <Box>
              {mockBillingThroughRelationships.length === 0 ? (
                <Paper withBorder p="lg">
                  <EnrollmentEmptyState variant="no-enrollments" />
                </Paper>
              ) : (
                <Paper withBorder p="lg">
                  <BillingThroughTable relationships={mockBillingThroughRelationships} />
                </Paper>
              )}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value={ENROLLMENT_PDF_TAB} mt="md">
            <Suspense fallback={<Loading />}>
              <EnrollmentPdfRunsPanel />
            </Suspense>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
